import React from 'react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { ArrowRight, Sparkles, Zap } from 'lucide-react';
import { mockData } from '../../mock';
import MeshNetwork from '../MeshNetwork';
import PortfolioShowcase from '../PortfolioShowcase';

const Hero = ({ 
  headingRef, 
  handleHeadingMouseMove, 
  isHeadingHovered, 
  setIsHeadingHovered, 
  mousePosition, 
  scrollToContact, 
  scrollToPortfolio,
  scrollY 
}) => {
  return (
    <section className="relative pt-20 pb-24 px-4 md:px-6 bg-gradient-to-b from-[#00122A] via-[#0F294C] to-[#00122A] overflow-hidden min-h-screen flex items-center">
      {/* Base 44 Style: Floating Particle Background */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="stars-container">
          {[...Array(150)].map((_, i) => (
            <div
              key={i}
              className="star"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 10}s`,
                animationDuration: `${6 + Math.random() * 10}s`
              }}
            />
          ))}
        </div>
      </div>
      
      {/* Depth-creating gradient glows */}
      <div className="absolute top-1/4 left-1/3 w-[900px] h-[900px] bg-[#3B82F6]/12 rounded-full blur-[140px] animate-float-slow"></div>
      <div className="absolute bottom-1/4 right-1/3 w-[800px] h-[800px] bg-[#2F6BFF]/18 rounded-full blur-[120px] animate-float-slow" style={{ animationDelay: '5s' }}></div>
      
      <div className="container mx-auto max-w-7xl relative z-10">
        <div className="text-center space-y-8 md:space-y-12">
          {/* Premium badge - Base 44 style */}
          <div className="inline-block animate-fadeInDown" style={{ animationDelay: '0.2s', opacity: 0 }}>
            <Badge className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#0F294C]/40 border border-[#3B82F6]/30 backdrop-blur-xl shadow-xl shadow-[#3B82F6]/5 hover:border-[#3B82F6]/60 transition-all duration-500">
              <div className="w-2 h-2 rounded-full bg-[#3B82F6] animate-pulse-dot"></div>
              <span className="text-sm font-medium text-white/90">
                Professional Web Design • London
              </span>
            </Badge>
          </div>

          {/* Main headline - Base 44 Premium Style */}
          <h1 
            ref={headingRef}
            className="text-5xl sm:text-6xl md:text-7xl lg:text-8xl xl:text-[6.5rem] font-extrabold leading-[1.05] max-w-6xl mx-auto cursor-default relative animate-fadeInUp px-4"
            style={{
              animationDelay: '0.4s',
              opacity: 0,
              letterSpacing: '-0.03em'
            }}
          >
            <span className="text-white">
              Build{' '}
              <span 
                className="relative inline-block"
                style={{
                  background: 'linear-gradient(135deg, #60A5FA 0%, #3B82F6 25%, #60A5FA 50%, #3B82F6 75%, #60A5FA 100%)',
                  backgroundSize: '200% auto',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                  filter: 'drop-shadow(0 0 50px rgba(59, 130, 246, 0.8)) drop-shadow(0 0 80px rgba(96, 165, 250, 0.5))',
                  animation: 'shimmerGlow 4s ease-in-out infinite'
                }}
              >
                AI-level
              </span>
              <br />
              Websites Built to Help
              <br />
              Local Businesses Grow
            </span>
          </h1>

          {/* Subheadline - Clean and spacious */}
          <p className="text-lg md:text-xl lg:text-2xl text-white/60 max-w-4xl mx-auto leading-relaxed animate-fadeInUpStagger px-4" style={{ animationDelay: '0.6s', opacity: 0, fontWeight: 400 }}>
            {mockData.hero.subheadline}
          </p>

          {/* CTA buttons - Base 44 premium style */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center mt-10 md:mt-14 animate-fadeInUpStagger px-4" style={{ animationDelay: '0.8s', opacity: 0 }}>
            <Button 
              onClick={scrollToContact}
              size="lg" 
              className="group relative w-full sm:w-auto bg-[#3B82F6] hover:bg-[#60A5FA] text-white text-base md:text-lg px-10 md:px-14 py-6 md:py-7 font-semibold rounded-xl hover:scale-[1.02] transition-all duration-300 shadow-[0_0_40px_rgba(59,130,246,0.4)] hover:shadow-[0_0_60px_rgba(96,165,250,0.6)]"
            >
              <span className="relative z-10 flex items-center justify-center gap-2">
                {mockData.hero.ctaText}
                <ArrowRight className="h-5 w-5 group-hover:translate-x-1 transition-transform duration-300" />
              </span>
            </Button>
            
            <Button 
              onClick={scrollToPortfolio}
              size="lg" 
              variant="outline" 
              className="group w-full sm:w-auto border-2 border-white/20 text-white hover:bg-white/5 hover:border-white/40 text-base md:text-lg px-10 md:px-14 py-6 md:py-7 font-semibold rounded-xl hover:scale-[1.02] transition-all duration-300 backdrop-blur-sm"
            >
              <span className="flex items-center justify-center gap-2">
                <Zap className="h-5 w-5 text-[#3B82F6] group-hover:rotate-12 transition-transform duration-300" />
                {mockData.hero.secondaryCta}
              </span>
            </Button>
          </div>

          {/* Trust badges - Minimal Base 44 style */}
          <div className="flex flex-wrap gap-4 md:gap-6 justify-center items-center mt-10 md:mt-14 animate-fadeInUpStagger px-4" style={{ animationDelay: '1s', opacity: 0 }}>
            {mockData.hero.trustBadges.map((badge, index) => (
              <div 
                key={index} 
                className="inline-flex items-center gap-2.5 px-5 py-2.5 bg-white/5 backdrop-blur-md rounded-full border border-white/10 hover:border-white/20 hover:bg-white/10 transition-all duration-300"
              >
                <div className="w-1.5 h-1.5 rounded-full bg-[#3B82F6] animate-pulse-dot"></div>
                <span className="text-sm font-medium text-white/80 whitespace-nowrap">{badge}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Enhanced Portfolio Showcase */}
        <div className="mt-16 md:mt-24">
          <PortfolioShowcase mousePosition={mousePosition} />
        </div>
      </div>
    </section>
  );
};

export default Hero;
